"use client";

import { Fragment, useEffect, useRef, useState } from "react";
import { useInView, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Workflow joué en boucle. Les nodes "app" portent le vrai logo (collection
// Iconify `logos:`), les nodes "core" une icône Solar teintée.
// Classes de couleur écrites en toutes lettres pour que Tailwind les génère.
const NODES = [
    {
        icon: "user-plus-bold-duotone",
        app: "Webhook",
        labelKey: "N8nLive_node_1",
        bg: "bg-rose-500/15",
        ring: "ring-rose-400/40",
        text: "text-rose-300",
        ping: "bg-rose-400",
    },
    {
        icon: "magnifer-bold",
        app: "HTTP Request",
        labelKey: "N8nLive_node_2",
        bg: "bg-amber-500/15",
        ring: "ring-amber-400/40",
        text: "text-amber-300",
        ping: "bg-amber-400",
    },
    {
        logo: "logos:notion-icon",
        app: "Notion",
        labelKey: "N8nLive_node_3",
        ping: "bg-sky-400",
    },
    {
        logo: "logos:google-gmail",
        app: "Gmail",
        labelKey: "N8nLive_node_4",
        ping: "bg-rose-400",
    },
    {
        logo: "logos:slack-icon",
        app: "Slack",
        labelKey: "N8nLive_node_5",
        ping: "bg-emerald-400",
    },
];

function stepState(activeStep, i) {
    if (activeStep === -1) return "idle";
    if (activeStep > i) return "done";
    if (activeStep === i) return "active";
    return "idle";
}

export default function N8nLiveWorkflow() {
    const t = useTranslations();
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const inView = useInView(ref, { margin: "-120px" });

    // activeStep : -1 au repos · 0..N-1 node en cours · N tous exécutés
    const [activeStep, setActiveStep] = useState(-1);

    useEffect(() => {
        const count = NODES.length;
        if (reduce) {
            setActiveStep(count);
            return;
        }
        if (!inView) return;

        const timers = [];
        let stopped = false;

        const cycle = () => {
            setActiveStep(-1);
            for (let i = 0; i < count; i++) {
                timers.push(setTimeout(() => setActiveStep(i), 900 + i * 750));
            }
            timers.push(setTimeout(() => setActiveStep(count), 900 + count * 750));
            timers.push(
                setTimeout(() => {
                    if (!stopped) cycle();
                }, 900 + count * 750 + 1800),
            );
        };
        timers.push(setTimeout(cycle, 500));

        return () => {
            stopped = true;
            timers.forEach(clearTimeout);
        };
    }, [inView, reduce]);

    return (
        <section id="n8n-live" className="pt-16" ref={ref}>
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("N8nLive_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("N8nLive_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-orange-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-purple-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre du workflow */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <SolarIcon
                        name="play-circle-bold-duotone"
                        className="w-4 h-4 text-orange-300"
                    />
                    <span className="text-[12px] font-mono text-gray-300">
                        {t("N8nLive_workflow_name")}
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("N8nLive_badge")}
                        </span>
                    </span>
                </div>

                {/* Zone canvas */}
                <div className="relative p-6 sm:p-10">
                    <div
                        className="absolute inset-0 opacity-[0.07]"
                        style={{
                            backgroundImage:
                                "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
                            backgroundSize: "26px 26px",
                        }}
                    />

                    <div className="relative flex flex-col lg:flex-row lg:items-stretch">
                        {NODES.map((node, i) => {
                            const state = stepState(activeStep, i);
                            return (
                                <Fragment key={node.labelKey}>
                                    <div
                                        className={`flex flex-row lg:flex-col lg:justify-center items-center gap-3 lg:gap-2.5 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-3 lg:p-4 lg:w-[150px] shrink-0 transition-opacity duration-300 ${
                                            state === "idle" ? "opacity-50" : "opacity-100"
                                        }`}
                                    >
                                        <div className="relative shrink-0">
                                            {state === "active" && (
                                                <span
                                                    className={`absolute inset-0 rounded-xl ${node.ping} opacity-40 animate-ping`}
                                                />
                                            )}
                                            <div
                                                className={`relative w-11 h-11 rounded-xl flex items-center justify-center ring-1 ${
                                                    node.logo
                                                        ? "bg-white ring-black/5"
                                                        : `${node.bg} ${node.ring}`
                                                }`}
                                            >
                                                {node.logo ? (
                                                    <Icon
                                                        icon={node.logo}
                                                        className="w-6 h-6"
                                                        aria-hidden="true"
                                                    />
                                                ) : (
                                                    <SolarIcon
                                                        name={node.icon}
                                                        className={`w-5 h-5 ${node.text}`}
                                                    />
                                                )}
                                            </div>
                                            {state === "done" && (
                                                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[9px] leading-none ring-2 ring-bgDark-800">
                                                    ✓
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-left lg:text-center min-w-0">
                                            <p className="text-[13px] font-semibold text-white leading-tight mb-0 lg:min-h-[2.4em] lg:flex lg:items-center lg:justify-center">
                                                {t(node.labelKey)}
                                            </p>
                                            <p className="text-[10px] text-gray-400 mb-0 mt-0.5 font-mono">
                                                {node.app}
                                            </p>
                                        </div>
                                    </div>

                                    {i < NODES.length - 1 && (
                                        <div className="flex items-center justify-center py-1.5 lg:py-0 lg:flex-1">
                                            <div
                                                className={`w-px h-6 lg:w-full lg:h-0.5 rounded-full transition-colors duration-500 ${
                                                    activeStep > i
                                                        ? "bg-gradient-to-b lg:bg-gradient-to-r from-orange-400 to-amber-400"
                                                        : "bg-white/15"
                                                }`}
                                            />
                                        </div>
                                    )}
                                </Fragment>
                            );
                        })}
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("N8nLive_caption")}
            </p>
        </section>
    );
}
